import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../lib/api';
import type {
  AuditEvent,
  AuditResponse,
  ChecklistItem,
  ChecklistResponse,
  DecisionLog,
  DecisionLogResponse,
  Finding,
  Project,
  ProjectSummary,
  Scan,
  TaskResponse,
} from '../types';

type Filter = 'all' | 'unresolved' | 'missing_evidence' | 'needs_review';

const REVIEWER_ROLES = new Set(['owner', 'admin', 'reviewer']);
const REVIEW_EVENT_TYPES = new Set([
  'project_review_requested',
  'project_changes_requested',
  'project_ready_for_export',
  'project_review_invalidated',
]);

const reviewStatusLabel: Record<Project['reviewStatus'], string> = {
  draft: 'Draft',
  review_requested: 'Review requested',
  changes_requested: 'Changes requested',
  ready_for_export: 'Ready for export',
};

const decisionTypeLabel: Record<DecisionLog['decisionType'], string> = {
  checklist_status_override: 'Checklist status decision',
  finding_review_status: 'Finding review decision',
  approval: 'Approval decision',
};

const approvalKey = (targetType: string, targetId: string) =>
  `${targetType}:${targetId}`;

const MINIMUM_RATIONALE_LENGTH = 20;

export default function ConsultantWorkspacePage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [checklist, setChecklist] = useState<ChecklistItem[]>([]);
  const [auditEvents, setAuditEvents] = useState<AuditEvent[]>([]);
  const [highFindings, setHighFindings] = useState<Finding[]>([]);
  const [decisionLogs, setDecisionLogs] = useState<DecisionLog[]>([]);
  const [filter, setFilter] = useState<Filter>('unresolved');
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
  const [changeRequest, setChangeRequest] = useState('');
  const [approvalRationales, setApprovalRationales] = useState<
    Record<string, string>
  >({});
  const [internalNotes, setInternalNotes] = useState<Record<string, string>>(
    {}
  );
  const [isSaving, setIsSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [focusIndex, setFocusIndex] = useState(0);

  const load = useCallback(async () => {
    if (!projectId) return;
    setError(null);
    try {
      const [
        projectResponse,
        checklistResponse,
        auditResponse,
        summaryResponse,
        decisionsResponse,
      ] = await Promise.all([
        api.get<Project>(`/api/projects/${projectId}`),
        api.get<ChecklistResponse>(`/api/projects/${projectId}/checklist`),
        api.get<AuditResponse>(`/api/projects/${projectId}/audit`),
        api.get<ProjectSummary>(`/api/projects/${projectId}/summary`),
        api.get<DecisionLogResponse>(`/api/projects/${projectId}/decisions`),
      ]);
      setProject(projectResponse.data);
      setChecklist(checklistResponse.data.checklistItems);
      setAuditEvents(auditResponse.data.auditEvents);
      setDecisionLogs(decisionsResponse.data.decisionLogs);
      if (summaryResponse.data.latestScanId) {
        const scanResponse = await api.get<Scan>(
          `/api/scans/${summaryResponse.data.latestScanId}`
        );
        setHighFindings(
          (scanResponse.data.findings ?? []).filter(
            finding =>
              ['high', 'medium'].includes(finding.severity) &&
              finding.reviewStatus !== 'rejected'
          )
        );
      } else {
        setHighFindings([]);
      }
    } catch {
      setError('The consultant workspace could not be loaded.');
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  const filteredItems = useMemo(() => {
    return checklist.filter(item => {
      if (filter === 'missing_evidence')
        return item.linkedEvidenceDocuments.length === 0;
      if (filter === 'needs_review') return item.status === 'needs_review';
      if (filter === 'unresolved') {
        return ['missing', 'needs_review', 'not_started'].includes(item.status);
      }
      return true;
    });
  }, [checklist, filter]);

  useEffect(() => setFocusIndex(0), [filter, filteredItems.length]);

  const reviewHistory = auditEvents.filter(event =>
    REVIEW_EVENT_TYPES.has(event.eventType)
  );

  const approvalStates = useMemo(() => {
    const states = new Map<string, string>();
    decisionLogs.forEach(decision => {
      if (decision.decisionType !== 'approval') return;
      const key = `${decision.targetType}:${decision.targetId}`;
      if (!states.has(key)) states.set(key, decision.newState ?? 'pending');
    });
    return states;
  }, [decisionLogs]);

  const approvalState = (targetType: string, targetId: string) =>
    approvalStates.get(`${targetType}:${targetId}`) ?? 'pending';

  const pendingRequiredApprovalCount = highFindings.filter(
    finding =>
      !finding.id || approvalState('finding', finding.id) !== 'approved'
  ).length;

  const changeApproval = async (
    targetType: 'finding' | 'checklist_item',
    targetId: string,
    revoke: boolean
  ) => {
    if (!projectId) return;
    const key = approvalKey(targetType, targetId);
    const rationale = approvalRationales[key]?.trim() ?? '';
    if (rationale.length < MINIMUM_RATIONALE_LENGTH) {
      setError(
        'Add a meaningful approval rationale of at least 20 characters. Explain what evidence you reviewed and why the decision is justified.'
      );
      return;
    }
    setIsSaving(`approval-${targetId}`);
    setError(null);
    try {
      await api.post(
        `/api/projects/${projectId}/approvals${revoke ? '/revoke' : ''}`,
        { targetType, targetId, rationale }
      );
      setApprovalRationales(values => ({ ...values, [key]: '' }));
      await load();
    } catch {
      setError('Could not update the approval decision.');
    } finally {
      setIsSaving(null);
    }
  };

  const updateChecklist = async (
    item: ChecklistItem,
    status: 'accepted' | 'needs_review'
  ) => {
    if (!projectId) return;
    const rationale = internalNotes[item.id]?.trim() ?? '';
    if (status === 'accepted' && rationale.length < MINIMUM_RATIONALE_LENGTH) {
      setError(
        'Add an internal review note of at least 20 characters before accepting. Explain what evidence supports the decision.'
      );
      return;
    }
    setIsSaving(item.id);
    setError(null);
    try {
      const response = await api.patch<ChecklistItem>(
        `/api/projects/${projectId}/checklist/${item.id}/status`,
        { status, rationale }
      );
      setChecklist(items =>
        items.map(value => (value.id === item.id ? response.data : value))
      );
      await load();
    } catch {
      setError('Could not update the checklist item.');
    } finally {
      setIsSaving(null);
    }
  };

  const createTask = async (item: ChecklistItem) => {
    if (!projectId) return;
    setIsSaving(`task-${item.id}`);
    setError(null);
    try {
      await api.post<TaskResponse>(`/api/projects/${projectId}/tasks`, {
        title: `Consultant review: ${item.title}`,
        description: `Internal review note: ${internalNotes[item.id]?.trim() ?? ''}`,
        priority: 'high',
        linkedObjectType: 'checklist_item',
        linkedObjectId: item.id,
      });
      await load();
    } catch {
      setError('Could not create the linked review task.');
    } finally {
      setIsSaving(null);
    }
  };

  const transition = async (action: 'request_changes' | 'mark_ready') => {
    if (!projectId) return;
    if (
      action === 'request_changes' &&
      (!changeRequest.trim() || selectedItemIds.length === 0)
    ) {
      setError(
        'Select at least one checklist item and describe the required changes.'
      );
      return;
    }
    setIsSaving(action);
    setError(null);
    try {
      const response = await api.post<Project>(
        `/api/projects/${projectId}/review-transition`,
        {
          action,
          rationale: changeRequest.trim(),
          checklistItemIds: selectedItemIds,
        }
      );
      setProject(response.data);
      setChangeRequest('');
      setSelectedItemIds([]);
      await load();
    } catch {
      setError('Could not update the project review state.');
    } finally {
      setIsSaving(null);
    }
  };

  if (!project) {
    return (
      <p style={{ padding: 48, color: 'var(--text-muted)' }}>
        {error ?? 'Loading consultant workspace…'}
      </p>
    );
  }
  if (!REVIEWER_ROLES.has(project.currentUserRole)) {
    return (
      <section className="route-error-card animate-fade-in">
        <span className="route-error-eyebrow">Consultant mode</span>
        <h1 className="route-error-title">Consultant access required</h1>
        <p className="route-error-message">
          Only owners, admins, and reviewers can access this project review
          workspace.
        </p>
        <Link className="button button-primary" to={`/projects/${project.id}`}>
          Back to project
        </Link>
      </section>
    );
  }

  const focusedItem = filteredItems[focusIndex];
  return (
    <div
      className="animate-fade-in"
      style={{ maxWidth: 1180, margin: '0 auto' }}
    >
      <div className="page-intro">
        <div className="page-eyebrow">CONSULTANT MODE · SINGLE PROJECT</div>
        <h1 style={{ fontFamily: 'var(--font-display)', margin: '6px 0' }}>
          {project.name}
        </h1>
        <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
          Dense triage for unresolved requirements, evidence, internal notes,
          and review handoffs.
        </p>
      </div>

      <section
        className="results-panel"
        style={{ margin: '24px 0', padding: 24 }}
      >
        <div className="results-toolbar">
          <div>
            <strong>
              Handoff status: {reviewStatusLabel[project.reviewStatus]}
            </strong>
            <p style={{ margin: '6px 0 0', color: 'var(--text-muted)' }}>
              {reviewHistory.length} recorded review cycle event
              {reviewHistory.length === 1 ? '' : 's'}.
            </p>
          </div>
          <Link
            className="button button-secondary"
            to={`/projects/${project.id}`}
          >
            Builder view
          </Link>
        </div>
        {project.reviewStatus === 'review_requested' && (
          <div style={{ display: 'grid', gap: 12, marginTop: 18 }}>
            <label style={{ display: 'grid', gap: 6 }}>
              <span>
                Internal change request (never included in report exports)
              </span>
              <textarea
                rows={3}
                maxLength={2000}
                value={changeRequest}
                onChange={event => setChangeRequest(event.target.value)}
                placeholder="Describe the evidence or checklist corrections needed."
              />
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              <button
                className="button button-primary"
                type="button"
                disabled={isSaving !== null}
                onClick={() => void transition('request_changes')}
              >
                Request changes for selected items
              </button>
              <button
                className="button button-secondary"
                type="button"
                disabled={isSaving !== null || pendingRequiredApprovalCount > 0}
                onClick={() => void transition('mark_ready')}
              >
                Mark ready for export
              </button>
            </div>
            {pendingRequiredApprovalCount > 0 && (
              <p
                role="status"
                style={{ margin: 0, color: 'var(--text-muted)' }}
              >
                Approve all medium- and high-severity findings before marking
                this project ready for export ({pendingRequiredApprovalCount}{' '}
                remaining).
              </p>
            )}
          </div>
        )}
      </section>

      {error && (
        <p role="alert" style={{ color: 'var(--danger)' }}>
          {error}
        </p>
      )}
      <div
        className="consultant-filter-tabs"
        style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}
      >
        {(
          ['unresolved', 'needs_review', 'missing_evidence', 'all'] as Filter[]
        ).map(value => (
          <button
            key={value}
            className="button button-secondary"
            type="button"
            onClick={() => setFilter(value)}
            style={{
              borderColor:
                filter === value ? 'var(--accent-primary)' : undefined,
            }}
          >
            {value.split('_').join(' ')}
          </button>
        ))}
        <button
          className="button button-secondary"
          type="button"
          disabled={filteredItems.length < 2}
          onClick={() =>
            setFocusIndex(index => (index + 1) % filteredItems.length)
          }
        >
          Next flagged item
        </button>
      </div>

      {highFindings.length > 0 && (
        <section
          className="results-panel"
          style={{ padding: 20, marginBottom: 24 }}
        >
          <h2 style={{ fontFamily: 'var(--font-display)', marginTop: 0 }}>
            Medium and high findings
          </h2>
          <ul style={{ display: 'grid', gap: 8, margin: 0, paddingLeft: 20 }}>
            {highFindings.map(finding => (
              <li key={finding.id ?? finding.title}>
                <strong>{finding.title}</strong>
                {finding.recommendation ? ` — ${finding.recommendation}` : ''}
                {finding.id && (
                  <div
                    style={{
                      marginTop: 6,
                      display: 'grid',
                      gap: 8,
                    }}
                  >
                    <label style={{ display: 'grid', gap: 6 }}>
                      <span>Approval rationale for {finding.title}</span>
                      <textarea
                        rows={2}
                        minLength={MINIMUM_RATIONALE_LENGTH}
                        maxLength={2000}
                        value={
                          approvalRationales[
                            approvalKey('finding', finding.id)
                          ] ?? ''
                        }
                        onChange={event =>
                          setApprovalRationales(values => ({
                            ...values,
                            [approvalKey('finding', finding.id!)]:
                              event.target.value,
                          }))
                        }
                        placeholder="Explain this finding approval or revocation."
                      />
                      <small style={{ color: 'var(--text-muted)' }}>
                        At least 20 characters. Describe the evidence reviewed
                        and why it supports this decision.
                      </small>
                    </label>
                    <div
                      style={{
                        display: 'flex',
                        gap: 8,
                        alignItems: 'center',
                        flexWrap: 'wrap',
                      }}
                    >
                      <span style={{ color: 'var(--text-muted)' }}>
                        Approval: {approvalState('finding', finding.id)}
                      </span>
                      <button
                        className="button button-secondary"
                        type="button"
                        disabled={
                          isSaving !== null ||
                          approvalState('finding', finding.id) === 'approved'
                        }
                        onClick={() =>
                          void changeApproval('finding', finding.id!, false)
                        }
                      >
                        Approve finding
                      </button>
                      <button
                        className="button button-secondary"
                        type="button"
                        disabled={
                          isSaving !== null ||
                          approvalState('finding', finding.id) !== 'approved'
                        }
                        onClick={() =>
                          void changeApproval('finding', finding.id!, true)
                        }
                      >
                        Revoke approval
                      </button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section
        className="results-panel"
        style={{ padding: 24, marginBottom: 24 }}
      >
        <h2 style={{ fontFamily: 'var(--font-display)', marginTop: 0 }}>
          Checklist triage
        </h2>
        {focusedItem && (
          <p style={{ color: 'var(--text-muted)' }}>
            Focused item: {focusedItem.title}
          </p>
        )}
        <div style={{ display: 'grid', gap: 12 }}>
          {filteredItems.map((item, index) => {
            const selected = selectedItemIds.includes(item.id);
            return (
              <article
                key={item.id}
                style={{
                  border:
                    index === focusIndex
                      ? '1px solid var(--accent-primary)'
                      : '1px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  padding: 16,
                }}
              >
                <label
                  style={{ display: 'flex', gap: 10, alignItems: 'start' }}
                >
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() =>
                      setSelectedItemIds(ids =>
                        selected
                          ? ids.filter(id => id !== item.id)
                          : [...ids, item.id]
                      )
                    }
                  />
                  <span>
                    <strong>{item.title}</strong>
                    <br />
                    <small>
                      {item.requirementReference} ·{' '}
                      {item.status.replace('_', ' ')} · approval{' '}
                      {approvalState('checklist_item', item.id)} ·{' '}
                      {item.linkedEvidenceDocuments.length} evidence file
                      {item.linkedEvidenceDocuments.length === 1 ? '' : 's'}
                    </small>
                  </span>
                </label>
                <label style={{ display: 'grid', gap: 6, marginTop: 12 }}>
                  <span>Internal consultant note for {item.title}</span>
                  <textarea
                    rows={2}
                    minLength={MINIMUM_RATIONALE_LENGTH}
                    maxLength={2000}
                    value={internalNotes[item.id] ?? ''}
                    onChange={event =>
                      setInternalNotes(notes => ({
                        ...notes,
                        [item.id]: event.target.value,
                      }))
                    }
                    placeholder="Used when changing status or creating a review task."
                  />
                  <small style={{ color: 'var(--text-muted)' }}>
                    At least 20 characters are required when accepting an item.
                  </small>
                </label>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 8,
                    marginTop: 10,
                  }}
                >
                  <button
                    className="button button-secondary"
                    type="button"
                    disabled={isSaving !== null}
                    onClick={() => void updateChecklist(item, 'needs_review')}
                  >
                    Mark needs review
                  </button>
                  <button
                    className="button button-secondary"
                    type="button"
                    disabled={isSaving !== null}
                    onClick={() => void updateChecklist(item, 'accepted')}
                  >
                    Accept with note
                  </button>
                  <button
                    className="button button-secondary"
                    type="button"
                    disabled={isSaving !== null}
                    onClick={() => void createTask(item)}
                  >
                    Create linked task
                  </button>
                </div>
                <label
                  style={{
                    display: 'grid',
                    gap: 6,
                    marginTop: 12,
                  }}
                >
                  <span>Approval rationale for {item.title}</span>
                  <textarea
                    rows={2}
                    minLength={MINIMUM_RATIONALE_LENGTH}
                    maxLength={2000}
                    value={
                      approvalRationales[
                        approvalKey('checklist_item', item.id)
                      ] ?? ''
                    }
                    onChange={event =>
                      setApprovalRationales(values => ({
                        ...values,
                        [approvalKey('checklist_item', item.id)]:
                          event.target.value,
                      }))
                    }
                    placeholder="Explain this checklist approval or revocation."
                  />
                  <small style={{ color: 'var(--text-muted)' }}>
                    At least 20 characters. Describe the evidence reviewed and
                    why it supports this decision.
                  </small>
                </label>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 8,
                    marginTop: 10,
                  }}
                >
                  <span style={{ color: 'var(--text-muted)' }}>
                    Approval: {approvalState('checklist_item', item.id)}
                  </span>
                  <button
                    className="button button-secondary"
                    type="button"
                    disabled={
                      isSaving !== null ||
                      approvalState('checklist_item', item.id) === 'approved'
                    }
                    onClick={() =>
                      void changeApproval('checklist_item', item.id, false)
                    }
                  >
                    Approve item
                  </button>
                  <button
                    className="button button-secondary"
                    type="button"
                    disabled={
                      isSaving !== null ||
                      approvalState('checklist_item', item.id) !== 'approved'
                    }
                    onClick={() =>
                      void changeApproval('checklist_item', item.id, true)
                    }
                  >
                    Revoke item approval
                  </button>
                </div>
              </article>
            );
          })}
          {filteredItems.length === 0 && (
            <p style={{ color: 'var(--text-muted)' }}>
              No checklist items match this filter.
            </p>
          )}
        </div>
      </section>

      <section
        className="results-panel"
        style={{ padding: 24, marginBottom: 24 }}
        aria-labelledby="consultant-decision-log-heading"
      >
        <h2
          id="consultant-decision-log-heading"
          style={{ fontFamily: 'var(--font-display)', marginTop: 0 }}
        >
          Decision log
        </h2>
        <p style={{ color: 'var(--text-muted)', marginTop: -6 }}>
          Previous rationale-backed decisions are available here without leaving
          consultant mode.
        </p>
        {decisionLogs.length === 0 ? (
          <p style={{ color: 'var(--text-muted)' }}>
            No compliance decisions have been recorded yet.
          </p>
        ) : (
          <ol
            style={{
              listStyle: 'none',
              display: 'grid',
              gap: 12,
              margin: '18px 0 0',
              padding: 0,
            }}
          >
            {decisionLogs.map(decision => (
              <li
                key={decision.id}
                style={{
                  borderLeft: '3px solid var(--accent-primary)',
                  paddingLeft: 14,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 12,
                    flexWrap: 'wrap',
                  }}
                >
                  <strong>{decisionTypeLabel[decision.decisionType]}</strong>
                  <time
                    dateTime={decision.createdAt}
                    style={{ color: 'var(--text-muted)', fontSize: 12 }}
                  >
                    {new Date(decision.createdAt).toLocaleString()}
                  </time>
                </div>
                <p style={{ margin: '5px 0 0' }}>
                  {decision.targetType.replace(/_/g, ' ')} ·{' '}
                  {decision.previousState && decision.newState
                    ? `${decision.previousState.replace(/_/g, ' ')} → ${decision.newState.replace(/_/g, ' ')}`
                    : decision.newState?.replace(/_/g, ' ') || 'recorded'}
                </p>
                <p
                  style={{
                    margin: '5px 0 0',
                    color: 'var(--text-secondary)',
                  }}
                >
                  {decision.rationale || 'No rationale recorded.'}
                </p>
                <p
                  style={{
                    margin: '5px 0 0',
                    color: 'var(--text-muted)',
                    fontSize: 12,
                  }}
                >
                  By {decision.actorName || 'System'}
                </p>
              </li>
            ))}
          </ol>
        )}
      </section>

      <section className="results-panel" style={{ padding: 24 }}>
        <h2 style={{ fontFamily: 'var(--font-display)', marginTop: 0 }}>
          Review handoff history
        </h2>
        {reviewHistory.length === 0 ? (
          <p style={{ color: 'var(--text-muted)' }}>
            No review handoffs have been recorded yet.
          </p>
        ) : (
          <ol style={{ display: 'grid', gap: 10, paddingLeft: 20 }}>
            {reviewHistory.map(event => (
              <li key={event.id}>
                <strong>{event.eventType.replace(/_/g, ' ')}</strong> ·{' '}
                {event.actorName || 'System'} ·{' '}
                {new Date(event.createdAt).toLocaleString()}{' '}
                {typeof event.metadata.rationale === 'string' &&
                event.metadata.rationale
                  ? `— ${event.metadata.rationale}`
                  : ''}
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}
